// Platform Events — read-only feed of significant platform happenings.
//
// Source-of-truth reference: /var/www/kitchenos/admin-standalone.html
//   page section: ~line 1442 (#page-events)
//   data:         /var/www/kitchenos-api/data/catalogue_events.json
//
// Backends:
//   GET /api/admin/events            — INTENDED, not yet implemented
//                                      (master doc flags as missing).
//                                      We try this first.
//   GET /api/admin/catalogue-events  — EXISTS (server.js ~1134) — used as
//                                      fallback so the page has data today.
//
// Both endpoints are READ-ONLY. The page filters in-memory by type and date.

import { ApiError } from "@tkos/api-client";

import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

import "./styles.css";

// ── Types ────────────────────────────────────────────────────────────────────

type EventType =
  | "retailer_signup"
  | "transaction"
  | "fraud"
  | "deployment"
  | "downtime"
  | "catalogue"
  | "other";

interface ServerEvent {
  id?: string;
  type?: string;
  message?: string;
  text?: string;
  description?: string;
  at?: string | number;
  timestamp?: string | number;
  createdAt?: string | number;
  retailerId?: string;
  meta?: Record<string, unknown>;
}

interface PlatformEvent {
  id: string;
  type: EventType;
  message: string;
  whenMs: number;
  retailerId: string | null;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function coerceType(raw: unknown): EventType {
  if (typeof raw !== "string") return "other";
  const v = raw.toLowerCase();
  if (v.includes("signup") || v.includes("retailer_added")) return "retailer_signup";
  if (v.includes("transaction") || v.includes("payment")) return "transaction";
  if (v.includes("fraud") || v.includes("alert")) return "fraud";
  if (v.includes("deploy") || v.includes("release")) return "deployment";
  if (v.includes("downtime") || v.includes("outage")) return "downtime";
  if (v.includes("catalogue") || v.includes("import")) return "catalogue";
  return "other";
}

function coerceTime(raw: unknown): number {
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return raw < 1e12 ? raw * 1000 : raw;
  }
  if (typeof raw === "string" && raw.trim()) {
    const t = Date.parse(raw);
    if (Number.isFinite(t)) return t;
  }
  return Date.now();
}

let idCounter = 0;
function normalise(e: ServerEvent): PlatformEvent {
  const msg =
    String(e.message ?? e.text ?? e.description ?? "").trim() ||
    "(no message)";
  return {
    id: String(e.id ?? `evt_${++idCounter}`),
    type: coerceType(e.type),
    message: msg,
    whenMs: coerceTime(e.at ?? e.timestamp ?? e.createdAt),
    retailerId: typeof e.retailerId === "string" ? e.retailerId : null,
  };
}

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

const dateFmt = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
});

const RTF = new Intl.RelativeTimeFormat("en-GB", { numeric: "auto" });
function relTime(ms: number): string {
  const diffSec = Math.round((ms - Date.now()) / 1000);
  const abs = Math.abs(diffSec);
  if (abs < 60) return RTF.format(diffSec, "second");
  if (abs < 3600) return RTF.format(Math.round(diffSec / 60), "minute");
  if (abs < 86400) return RTF.format(Math.round(diffSec / 3600), "hour");
  if (abs < 86400 * 30) return RTF.format(Math.round(diffSec / 86400), "day");
  return RTF.format(Math.round(diffSec / (86400 * 30)), "month");
}

const TYPE_META: Record<
  EventType,
  { label: string; pill: string; icon: string }
> = {
  retailer_signup: { label: "Retailer signup", pill: "adm-pill-ok", icon: "🏪" },
  transaction: { label: "Transaction", pill: "adm-pill-info", icon: "💷" },
  fraud: { label: "Fraud flag", pill: "adm-pill-danger", icon: "🚨" },
  deployment: { label: "Deployment", pill: "adm-pill-info", icon: "🚀" },
  downtime: { label: "Downtime", pill: "adm-pill-warn", icon: "⚠️" },
  catalogue: { label: "Catalogue", pill: "adm-pill-info", icon: "📦" },
  other: { label: "Event", pill: "", icon: "•" },
};

type TypeFilter = "all" | EventType;
type DateFilter = "all" | "24h" | "7d" | "30d";

const TYPE_FILTERS: ReadonlyArray<{ value: TypeFilter; label: string }> = [
  { value: "all", label: "All types" },
  { value: "retailer_signup", label: "Retailer signups" },
  { value: "transaction", label: "Transactions" },
  { value: "fraud", label: "Fraud flags" },
  { value: "deployment", label: "Deployments" },
  { value: "downtime", label: "Downtime" },
  { value: "catalogue", label: "Catalogue" },
  { value: "other", label: "Other" },
];

const DATE_FILTERS: ReadonlyArray<{ value: DateFilter; label: string }> = [
  { value: "all", label: "All time" },
  { value: "24h", label: "Last 24h" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
];

// ── Page ─────────────────────────────────────────────────────────────────────

const eventsPage: Page = {
  name: "events",
  label: "Events",
  icon: "📅",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    const ac = new AbortController();
    let events: PlatformEvent[] = [];
    let typeFilter: TypeFilter = "all";
    let dateFilter: DateFilter = "all";
    let loading = true;
    let error: string | null = null;
    let sourceLabel = "";

    const root = document.createElement("section");
    root.className = "aevt-page";
    root.innerHTML = `
      <header class="aevt-header">
        <h1>Platform Events</h1>
        <p>Platform-wide significant events &mdash; signups, transactions, fraud flags, deployments, downtime.</p>
      </header>

      <div class="aevt-toolbar">
        <select class="adm-select aevt-type" data-role="type" aria-label="Filter by type">
          ${TYPE_FILTERS.map(
            (o) => `<option value="${o.value}">${esc(o.label)}</option>`,
          ).join("")}
        </select>
        <select class="adm-select aevt-date" data-role="date" aria-label="Filter by date">
          ${DATE_FILTERS.map(
            (o) => `<option value="${o.value}">${esc(o.label)}</option>`,
          ).join("")}
        </select>
        <span class="aevt-spacer"></span>
        <span class="aevt-source" data-role="source"></span>
      </div>

      <div class="aevt-list" data-role="list" aria-live="polite"></div>
    `;
    container.appendChild(root);

    const typeEl = root.querySelector<HTMLSelectElement>("[data-role='type']")!;
    const dateEl = root.querySelector<HTMLSelectElement>("[data-role='date']")!;
    const listEl = root.querySelector<HTMLDivElement>("[data-role='list']")!;
    const srcEl = root.querySelector<HTMLSpanElement>("[data-role='source']")!;

    function render(): void {
      srcEl.textContent = sourceLabel;
      if (loading) {
        listEl.innerHTML = `<div class="adm-loading">Loading events…</div>`;
        return;
      }
      if (error) {
        listEl.innerHTML = `<div class="adm-error">${esc(error)}</div>`;
        return;
      }
      const now = Date.now();
      const cutoff =
        dateFilter === "24h"
          ? now - 86400_000
          : dateFilter === "7d"
            ? now - 7 * 86400_000
            : dateFilter === "30d"
              ? now - 30 * 86400_000
              : 0;
      const visible = events
        .filter((e) => typeFilter === "all" || e.type === typeFilter)
        .filter((e) => cutoff === 0 || e.whenMs >= cutoff)
        .sort((a, b) => b.whenMs - a.whenMs);
      if (visible.length === 0) {
        listEl.innerHTML = `<div class="adm-empty">No events match these filters.</div>`;
        return;
      }
      listEl.innerHTML = visible
        .map((e) => {
          const meta = TYPE_META[e.type];
          return `
        <div class="aevt-row">
          <div class="aevt-icon">${meta.icon}</div>
          <div class="aevt-body">
            <div class="aevt-line">
              <span class="adm-pill ${meta.pill}">${esc(meta.label)}</span>
              <span class="aevt-msg">${esc(e.message)}</span>
            </div>
            <div class="aevt-meta">
              ${e.retailerId ? `<span class="aevt-retailer">${esc(e.retailerId)}</span>` : ""}
              <span class="aevt-time" title="${esc(dateFmt.format(e.whenMs))}">${esc(relTime(e.whenMs))}</span>
            </div>
          </div>
        </div>`;
        })
        .join("");
    }

    async function load(): Promise<void> {
      loading = true;
      error = null;
      sourceLabel = "";
      render();
      // Try the canonical endpoint first; fall back to catalogue-events.
      try {
        const raw = await ctx.api<ServerEvent[]>("/admin/events", { signal: ac.signal });
        events = (Array.isArray(raw) ? raw : []).map(normalise);
        sourceLabel = `${events.length} events`;
        loading = false;
        render();
        return;
      } catch (err) {
        if (isAbortError(err)) return;
        // 404 expected today; fall through to fallback.
      }
      try {
        const raw = await ctx.api<ServerEvent[]>("/admin/catalogue-events", {
          signal: ac.signal,
        });
        events = (Array.isArray(raw) ? raw : []).map((e) => ({
          ...normalise(e),
          type: "catalogue" as EventType,
        }));
        sourceLabel = `${events.length} catalogue events (no general feed yet)`;
        loading = false;
        render();
      } catch (err) {
        if (isAbortError(err)) return;
        loading = false;
        error =
          err instanceof ApiError
            ? `Couldn't load events (${err.status}).`
            : "Couldn't load events.";
        render();
      }
    }

    const onType = (): void => {
      typeFilter = (typeEl.value as TypeFilter) || "all";
      render();
    };
    const onDate = (): void => {
      dateFilter = (dateEl.value as DateFilter) || "all";
      render();
    };

    typeEl.addEventListener("change", onType);
    dateEl.addEventListener("change", onDate);

    void load();

    return () => {
      ac.abort();
      typeEl.removeEventListener("change", onType);
      dateEl.removeEventListener("change", onDate);
      root.remove();
    };
  },
};

registerPage(eventsPage);
export default eventsPage;
